import React, { useState, useEffect, useCallback } from 'react';
import { getPortfolioLMS, getTrainingModules, createTrainingModule, updateTrainingModule, deleteTrainingModule, getOrganizations, createTrainingAssignment } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import {
  FileText,
  GraduationCap,
  CheckCircle,
  AlertTriangle,
  Building2,
  Loader2,
  Calendar,
  Eye,
  Globe,
  Video,
  BookOpen,
  ClipboardCheck,
  Send,
  Target,
  TrendingUp,
  Users,
  Plus,
  Edit,
  Trash2,
  Clock,
  Play,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Training categories
const TRAINING_CATEGORIES = [
  'Safety Training',
  'Compliance',
  'Professional Development',
  'Emergency Response',
  'Risk Management',
  'Policy Implementation',
  'Leadership',
  'Technical Skills',
];

const TYPE_COLORS = {
  video: '#93c5fd',
  module: '#3b82f6',
  quiz: '#93c5fd',
  guide: '#10b981',
  document: '#f59e0b',
};

const CONTENT_TYPE_ICONS = {
  video: Video,
  module: BookOpen,
  quiz: ClipboardCheck,
  guide: FileText,
  document: FileText,
};

export default function PortfolioLMS() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Training modules management state
  const [trainingModules, setTrainingModules] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    content_type: 'video',
    category: '',
    content_url: '',
    duration_minutes: 0,
    passing_score: 80,
  });
  
  const [assignForm, setAssignForm] = useState({
    organization_id: '',
    due_date: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [portfolioRes, modulesRes, orgsRes] = await Promise.all([
        getPortfolioLMS({ days: parseInt(period) }),
        getTrainingModules(),
        getOrganizations(),
      ]);
      setData(portfolioRes.data);
      setTrainingModules(modulesRes.data || []);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      console.error('Error fetching portfolio LMS data:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Reset form
  const resetModuleForm = () => {
    setModuleForm({
      title: '',
      description: '',
      content_type: 'video',
      category: '',
      content_url: '',
      duration_minutes: 0,
      passing_score: 80,
    });
  };
  
  // Create Training Module
  const handleCreateModule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTrainingModule(moduleForm);
      toast.success('Training module created successfully');
      setShowModuleDialog(false);
      resetModuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create training module');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Edit Training Module
  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title || '',
      description: module.description || '',
      content_type: module.content_type || 'video',
      category: module.category || '',
      content_url: module.content_url || '',
      duration_minutes: module.duration_minutes || 0,
      passing_score: module.passing_score || 80,
    });
    setShowModuleDialog(true);
  };
  
  // Update Training Module
  const handleUpdateModule = async (e) => {
    e.preventDefault();
    if (!editingModule) return;
    
    setSubmitting(true);
    try {
      await updateTrainingModule(editingModule.id, moduleForm);
      toast.success('Training module updated successfully');
      setShowModuleDialog(false);
      setEditingModule(null);
      resetModuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update training module');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Delete Training Module
  const handleDeleteModule = async (moduleId) => {
    if (!confirm('Are you sure you want to delete this training module?')) return;
    
    try {
      await deleteTrainingModule(moduleId);
      toast.success('Training module deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete training module');
    }
  };
  
  // Publish Training Module
  const handlePublishModule = async (moduleId) => {
    try {
      await updateTrainingModule(moduleId, { status: 'active' });
      toast.success('Training module published');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish training module');
    }
  };
  
  // Handle dialog close
  const handleModuleDialogClose = (open) => {
    if (!open) {
      setEditingModule(null);
      resetModuleForm();
    }
    setShowModuleDialog(open);
  };
  
  // Assign training to org
  const handleAssignTraining = async (e) => {
    e.preventDefault();
    if (!selectedModule || !assignForm.organization_id) return;
    
    setSubmitting(true);
    try {
      await createTrainingAssignment({
        training_module_id: selectedModule.id,
        organization_id: assignForm.organization_id,
        due_date: assignForm.due_date || null,
      });
      toast.success('Training assigned successfully');
      setShowAssignDialog(false);
      setSelectedModule(null);
      setAssignForm({ organization_id: '', due_date: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign training');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Prepare chart data
  const trainingTypeData = data?.training?.by_type ? Object.entries(data.training.by_type)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count,
      color: TYPE_COLORS[type] || '#6b7280',
    })) : [];

  const policyCategoryData = data?.policies?.by_category ? Object.entries(data.policies.by_category)
    .map(([category, count]) => ({
      category: category.length > 15 ? category.substring(0, 15) + '...' : category,
      count,
    })) : [];

  return (
    <div className="space-y-6" data-testid="portfolio-lms">
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Portfolio Learning & Governance</h2>
            <p className="text-sm text-muted-foreground">Manage training modules and track analytics across all organizations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setShowModuleDialog(true)} 
            className="btn-primary-glow"
            data-testid="create-training-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Training
          </Button>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs for Analytics and Training Management */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics">Analytics Overview</TabsTrigger>
          <TabsTrigger value="modules">Training Modules ({trainingModules.length})</TabsTrigger>
        </TabsList>
        
        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Policy Coverage</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.summary?.policy_coverage || 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <Progress value={data?.summary?.policy_coverage || 0} className="mt-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {data?.policies?.orgs_with_policies || 0} / {data?.summary?.total_organizations || 0} orgs
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Training Coverage</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.summary?.training_coverage || 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <Progress value={data?.summary?.training_coverage || 0} className="mt-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {data?.training?.orgs_with_training || 0} / {data?.summary?.total_organizations || 0} orgs
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Acknowledgement Rate</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.policies?.acknowledgement_rate || 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
            <Progress value={data?.policies?.acknowledgement_rate || 0} className="mt-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {data?.policies?.acknowledged || 0} / {data?.policies?.total_assignments || 0} assignments
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Training Completion</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.progress?.completion_rate || 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
            </div>
            <Progress value={data?.progress?.completion_rate || 0} className="mt-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {data?.progress?.completed || 0} / {data?.progress?.total_progress_records || 0} started
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Content Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Policies Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Platform Policies
            </CardTitle>
            <CardDescription>Global policies available for deployment</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <p className="text-2xl font-bold">{data?.policies?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{data?.policies?.active || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{data?.policies?.draft || 0}</p>
                <p className="text-xs text-muted-foreground">Draft</p>
              </div>
            </div>
            
            {/* Categories */}
            {policyCategoryData.length > 0 && (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={policyCategoryData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#93c5fd" radius={[0, 4, 4, 0]} name="Policies" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-500" />
              Training Library
            </CardTitle>
            <CardDescription>Learning content by type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <p className="text-2xl font-bold">{data?.training?.total_modules || 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{data?.training?.published || 0}</p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
              <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{data?.training?.total_assignments || 0}</p>
                <p className="text-xs text-muted-foreground">Assigned</p>
              </div>
            </div>
            
            {/* Content Types Pie */}
            {trainingTypeData.length > 0 && (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={trainingTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {trainingTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Deployed Policies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="w-5 h-5 text-orange-500" />
              Most Deployed Policies
            </CardTitle>
            <CardDescription>Policies with highest deployment across organizations</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.top_deployed_policies?.length > 0 ? (
              <div className="space-y-3">
                {data.top_deployed_policies.map((policy, idx) => (
                  <div 
                    key={policy.policy_id} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-600">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{policy.title}</p>
                        <p className="text-xs text-muted-foreground">{policy.category}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/30">
                      {policy.deployment_count} orgs
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Send className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No policy deployments yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Completed Training */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Most Completed Training
            </CardTitle>
            <CardDescription>Training modules with highest completion rates</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.top_completed_training?.length > 0 ? (
              <div className="space-y-3">
                {data.top_completed_training.map((training, idx) => {
                  const TypeIcon = CONTENT_TYPE_ICONS[training.content_type] || BookOpen;
                  return (
                    <div 
                      key={training.module_id} 
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          training.content_type === 'video' ? 'bg-red-500/20' :
                          training.content_type === 'quiz' ? 'bg-blue-500/20' :
                          'bg-blue-500/20'
                        )}>
                          <TypeIcon className={cn(
                            "w-4 h-4",
                            training.content_type === 'video' ? 'text-red-500' :
                            training.content_type === 'quiz' ? 'text-blue-500' :
                            'text-blue-500'
                          )} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{training.title}</p>
                          <p className="text-xs text-muted-foreground">{training.category}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                        {training.completion_count} completions
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No training completions yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attention Needed */}
      {(data?.attention_needed?.orgs_without_policies?.length > 0 || data?.attention_needed?.orgs_without_training?.length > 0) && (
        <Card className="border-yellow-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Organizations Needing Attention
            </CardTitle>
            <CardDescription>Organizations without policy or training assignments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data?.attention_needed?.orgs_without_policies?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Without Policies ({data.attention_needed.orgs_without_policies.length})
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {data.attention_needed.orgs_without_policies.slice(0, 5).map(org => (
                      <div key={org.id} className="flex items-center gap-2 p-2 bg-secondary/30 rounded text-sm">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span>{org.name}</span>
                        <Badge variant="outline" className="text-xs ml-auto">{org.org_type || 'sports'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {data?.attention_needed?.orgs_without_training?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-blue-500" />
                    Without Training ({data.attention_needed.orgs_without_training.length})
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {data.attention_needed.orgs_without_training.slice(0, 5).map(org => (
                      <div key={org.id} className="flex items-center gap-2 p-2 bg-secondary/30 rounded text-sm">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span>{org.name}</span>
                        <Badge variant="outline" className="text-xs ml-auto">{org.org_type || 'sports'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      {data?.recent_activity?.policy_deployments?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Recent Policy Deployments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_activity.policy_deployments.map((deployment, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Send className="w-4 h-4 text-orange-500" />
                    <div>
                      <p className="font-medium text-sm">{deployment.policy_title}</p>
                      <p className="text-xs text-muted-foreground">Deployed to {deployment.organization_name}</p>
                    </div>
                  </div>
                  {deployment.assigned_at && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(deployment.assigned_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill-down Prompt */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Eye className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          <strong>Drill-Down Available:</strong> Switch to Organization View and select an organization to see their specific policy assignments, training progress, and deploy new content.
        </AlertDescription>
      </Alert>
        </TabsContent>
        
        {/* Training Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          {trainingModules.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Training Modules</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first training module to get started
                </p>
                <Button onClick={() => setShowModuleDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Training Module
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingModules.map((module) => (
                <Card key={module.id} className="bg-card border-border hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Video className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base line-clamp-1">{module.title}</CardTitle>
                          <CardDescription className="text-xs">{module.category}</CardDescription>
                        </div>
                      </div>
                      <Badge variant={module.status === 'active' ? 'default' : 'secondary'}>
                        {module.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {module.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {module.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {module.passing_score}% to pass
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {module.content_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(module.content_url, '_blank')}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditModule(module)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedModule(module);
                          setShowAssignDialog(true);
                        }}
                      >
                        <Send className="w-3 h-3" />
                      </Button>
                      {module.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePublishModule(module.id)}
                        >
                          <CheckCircle className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteModule(module.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create/Edit Module Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={handleModuleDialogClose}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingModule ? 'Edit Training Module' : 'Create Training Module'}
            </DialogTitle>
            <DialogDescription>
              {editingModule ? 'Update the training module details' : 'Add a new training module to the platform'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={editingModule ? handleUpdateModule : handleCreateModule} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={moduleForm.title}
                onChange={(e) => setModuleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter module title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={moduleForm.description}
                onChange={(e) => setModuleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the training module"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={moduleForm.category}
                  onValueChange={(value) => setModuleForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAINING_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_type">Content Type</Label>
                <Select
                  value={moduleForm.content_type}
                  onValueChange={(value) => setModuleForm(prev => ({ ...prev, content_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="interactive">Interactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_url">Video URL (YouTube or direct link)</Label>
              <Input
                id="content_url"
                value={moduleForm.content_url}
                onChange={(e) => setModuleForm(prev => ({ ...prev, content_url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=... or video URL"
              />
              <p className="text-xs text-muted-foreground">
                Supports YouTube links or direct video URLs
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={moduleForm.duration_minutes}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                  min={0}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passing_score">Passing Score (%)</Label>
                <Input
                  id="passing_score"
                  type="number"
                  value={moduleForm.passing_score}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 80 }))}
                  min={0}
                  max={100}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleModuleDialogClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingModule ? 'Update Module' : 'Create Module'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Training Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Training</DialogTitle>
            <DialogDescription>
              Assign &ldquo;{selectedModule?.title}&rdquo; to an organization
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAssignTraining} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org">Organization *</Label>
              <Select
                value={assignForm.organization_id}
                onValueChange={(value) => setAssignForm(prev => ({ ...prev, organization_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date (optional)</Label>
              <Input
                id="due_date"
                type="date"
                value={assignForm.due_date}
                onChange={(e) => setAssignForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !assignForm.organization_id}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Assign Training
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
